import { useApiClient } from '_core/api/context';
import { CenteredSpinner } from '_core/feedback/centeredSpinner';
import { GenericErrorMessage } from '_core/feedback/genericErrorMessage';
import {
  selectIsFavoriteSellContract,
  toggleSellContractFavoriteStatus,
} from '_core/me/me';
import { parseJsonWithFallback } from '_core/parseJsonWithFallback';
import {
  TabsRouteNode,
  TabsRouteNodeExpander,
  TabsRouteNodeTab,
} from '_core/router5/tabsRouteNode';
import { useToaster } from '_core/toaster/toasterContext';
import { useAsyncData } from '_core/useAsyncData';
import { useErpDispatch, useErpSelector } from '_erp/redux';
import { Button, Intent } from '@blueprintjs/core';
import { fetchContractSell } from 'contractsSell/api';
import * as React from 'react';
import { useMemo } from 'react';
import { useRouteNode } from 'react-router5';

import { ContractsSellFormRouteWrapper } from './sellFormRouteWrapper';
import { ContractsSellViewAgreementsTab } from './sellViewAgreementsTab';
import { ContractsSellViewExpeditionProtocolsTab } from './sellViewExpeditionProtocolsTab';
import { ContractsSellViewMainTab } from './sellViewMainTab';
import { ContractsSellViewRentProtocolsTab } from './sellViewRentProtocolsTab';
import { ContractsSellViewRepairProtocolsTab } from './sellViewRepairProtocolsTab';
import { ContractsSellViewTechrunProtocolsTab } from './sellViewTechrunProtocolsTab';

export default function ContractsSellViewRoute() {
  const toaster = useToaster();
  const dispatch = useErpDispatch();
  const api = useApiClient();
  const { route } = useRouteNode('contracts.sell.view');
  const contractId = Number(route.params.id);

  const { data, error, isFetching, updateData } = useAsyncData(
    [contractId, api],
    () => fetchContractSell(api, contractId)
  );

  const isFavorite = useErpSelector(state =>
    selectIsFavoriteSellContract(contractId, state)
  );

  const listParams = useMemo(
    () => parseJsonWithFallback(route.params.listParams, {}),
    [route.params.listParams]
  );

  return !data ? (
    isFetching ? (
      <CenteredSpinner />
    ) : (
      <GenericErrorMessage error={error} />
    )
  ) : (
    <ContractsSellFormRouteWrapper listParams={listParams} title={data.number}>
      <TabsRouteNode
        nodeName="contracts.sell.view"
        preserveRouteParams={['id', 'listParams']}
      >
        <TabsRouteNodeTab id="main" title="Основные данные">
          <ContractsSellViewMainTab
            contract={data}
            updateContract={updatedContract =>
              updateData(() => updatedContract)
            }
          />
        </TabsRouteNodeTab>

        {data.hasExpedition && (
          <TabsRouteNodeTab
            id="expeditionProtocols"
            title="Протоколы экспедирования"
          >
            <ContractsSellViewExpeditionProtocolsTab contract={data} />
          </TabsRouteNodeTab>
        )}

        {data.hasTechrun && (
          <TabsRouteNodeTab id="techrunProtocols" title="Протоколы техрейса">
            <ContractsSellViewTechrunProtocolsTab contract={data} />
          </TabsRouteNodeTab>
        )}

        {data.hasRent && (
          <TabsRouteNodeTab id="rentProtocols" title="Протоколы аренды">
            <ContractsSellViewRentProtocolsTab contract={data} />
          </TabsRouteNodeTab>
        )}

        {data.hasRepair && (
          <TabsRouteNodeTab id="repairsProtocols" title="Протоколы ремонта">
            <ContractsSellViewRepairProtocolsTab contract={data} />
          </TabsRouteNodeTab>
        )}

        <TabsRouteNodeTab id="agreements" title="Дополнительные соглашения">
          <ContractsSellViewAgreementsTab contract={data} />
        </TabsRouteNodeTab>

        <TabsRouteNodeExpander />

        <Button
          icon={isFavorite ? 'star' : 'star-empty'}
          intent={Intent.SUCCESS}
          large
          minimal
          onClick={async () => {
            try {
              await dispatch(toggleSellContractFavoriteStatus(contractId));
            } catch (err) {
              toaster.show({
                icon: 'error',
                intent: Intent.DANGER,
                message:
                  'Не удалось изменить статус избранности исходящего договора',
              });

              throw err;
            }
          }}
        />
      </TabsRouteNode>
    </ContractsSellFormRouteWrapper>
  );
}
